import { useState, useEffect } from "react";
import { getApiUrl, getBaseUrl } from "@/config/appConfig";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy, Trash2, Key, Eye, EyeOff, RefreshCw, Globe, Mail, Calendar, Info, Check, X, Edit, Save, Zap } from "lucide-react";

// Using centralized config
const getAPI = () => getApiUrl();
const API = `${getAPI()}`;

export default function APIKeys() {
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    partner_name: "",
    email: "",
    description: "",
    allowed_domain: "",
    expires_in_days: ""
  });

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      toast.error("Failed to fetch API keys");
    }
  };

  const resetForm = () => {
    setFormData({
      partner_name: "",
      email: "",
      description: "",
      allowed_domain: "",
      expires_in_days: ""
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.partner_name) {
      toast.error("Please enter a partner name");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        partner_name: formData.partner_name,
        email: formData.email || null,
        description: formData.description || null,
        allowed_domain: formData.allowed_domain || null,
        expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null
      };
      
      await axios.post(`${API}/admin/api-keys`, payload);
      toast.success("API key created successfully");
      resetForm();
      setShowCreateForm(false);
      fetchAPIKeys();
    } catch (error) {
      toast.error("Failed to create API key");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (keyId) => {
    if (!formData.partner_name) {
      toast.error("Please enter a partner name");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        partner_name: formData.partner_name,
        email: formData.email || null,
        description: formData.description || null,
        allowed_domain: formData.allowed_domain || null,
        expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : 0
      };
      
      await axios.put(`${API}/admin/api-keys/${keyId}`, payload);
      toast.success("API key updated successfully");
      setEditingKey(null);
      resetForm();
      fetchAPIKeys();
    } catch (error) {
      toast.error("Failed to update API key");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (keyId) => {
    if (!window.confirm("Are you sure you want to deactivate this API key? It will stop working immediately.")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/api-keys/${keyId}/deactivate`);
      toast.success("API key deactivated");
      fetchAPIKeys();
    } catch (error) {
      toast.error("Failed to deactivate API key");
    }
  };

  const handleActivate = async (keyId) => {
    try {
      await axios.put(`${API}/admin/api-keys/${keyId}/activate`);
      toast.success("API key activated");
      fetchAPIKeys();
    } catch (error) {
      toast.error("Failed to activate API key");
    }
  };

  const handleDelete = async (keyId) => {
    if (!window.confirm("Are you sure you want to PERMANENTLY DELETE this API key? This action cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/api-keys/${keyId}/delete`);
      toast.success("API key deleted permanently");
      fetchAPIKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete API key");
    }
  };

  const handleRegenerate = async (keyId) => {
    if (!window.confirm("Are you sure you want to regenerate this API key? The old key will stop working immediately and a new key will be generated.")) {
      return;
    }

    try {
      await axios.post(`${API}/admin/api-keys/${keyId}/regenerate`);
      toast.success("API key regenerated successfully");
      fetchAPIKeys();
    } catch (error) {
      toast.error("Failed to regenerate API key");
    }
  };

  const startEditing = (apiKey) => {
    setEditingKey(apiKey.id);
    setFormData({
      partner_name: apiKey.partner_name || "",
      email: apiKey.email || "",
      description: apiKey.description || "",
      allowed_domain: apiKey.allowed_domain || "",
      expires_in_days: ""
    });
    setShowCreateForm(false);
  };

  const cancelEditing = () => {
    setEditingKey(null);
    resetForm();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("API key copied to clipboard");
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskKey = (key) => {
    if (!key) return "";
    return key.substring(0, 7) + "•".repeat(20) + key.substring(key.length - 4);
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Button
                  onClick={() => navigate('/')}
                  variant="ghost"
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 -ml-2"
                  data-testid="back-to-dashboard"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </div>
              <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                API Keys Management
              </h1>
              <p className="text-slate-600 mt-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                Generate and manage API keys for external integrations
              </p>
            </div>
            <Button
              onClick={() => {
                setShowCreateForm(!showCreateForm);
                setEditingKey(null);
                resetForm();
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="create-api-key-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create API Key
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* 🚀 Quick Start Card — clear, 3-step onboarding (mobile-first) */}
        <Card className="p-3 sm:p-6 mb-6 border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50" data-testid="api-key-quickstart">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-4">
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600 shrink-0" />
                <span>Get started in 3 steps</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">The fastest path from zero to a working checkout widget on your site.</p>
            </div>
            <a 
              href="/developer-docs" 
              target="_blank" 
              rel="noopener noreferrer"
              className="self-start sm:self-auto text-xs bg-white border border-violet-200 text-violet-700 hover:bg-violet-100 px-3 py-1.5 rounded-md whitespace-nowrap font-medium"
              data-testid="view-full-docs-link"
            >
              Full Docs →
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {/* Step 1 */}
            <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                <p className="font-semibold text-slate-900 text-sm">Create a key</p>
              </div>
              <p className="text-xs text-slate-600 mb-3 leading-relaxed">Click the button below, name it (e.g., "Production"), copy the key once — we can't show it again.</p>
              <Button
                onClick={() => setShowCreateForm(true)}
                size="sm"
                className="w-full bg-violet-600 hover:bg-violet-700 text-white h-8"
                data-testid="quickstart-create-key"
              >
                <Plus className="w-4 h-4 mr-1" /> New Key
              </Button>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                <p className="font-semibold text-slate-900 text-sm">Drop the SDK on your page</p>
              </div>
              <p className="text-xs text-slate-600 mb-2">Paste these 3 lines anywhere on your site:</p>
              <pre className="text-[10px] bg-slate-900 text-emerald-300 rounded p-2 overflow-x-auto whitespace-pre leading-tight max-w-full" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{`<script src="${getBaseUrl()}/sdk/seatpilot.js"></script>
<script>
  const sp = SeatPilot.init({ apiKey: 'sk_...' });
</script>`}</pre>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                <p className="font-semibold text-slate-900 text-sm">Show seats & take bookings</p>
              </div>
              <p className="text-xs text-slate-600 mb-2">Embed the widget for any layout:</p>
              <pre className="text-[10px] bg-slate-900 text-emerald-300 rounded p-2 overflow-x-auto whitespace-pre leading-tight max-w-full" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{`<div id="seats"></div>
<script>
  sp.widget('#seats', 'LAYOUT_ID');
</script>`}</pre>
              <p className="text-[10px] text-slate-500 mt-2">No backend needed — keys auth via header.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 mt-4 p-3 bg-white/70 rounded-md border border-slate-200">
            <div className="shrink-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">4</div>
            <p className="text-xs text-slate-700 leading-relaxed">
              <strong>Want your organizers to design layouts themselves?</strong> Call <code className="bg-slate-100 border border-slate-200 rounded px-1 text-[10px] break-all">sp.builder('#el')</code> instead of <code className="bg-slate-100 border border-slate-200 rounded px-1 text-[10px] break-all">sp.widget()</code> — they get the exact same drag-and-drop Canvas you see on this site, embedded on your domain. Auto-saves every 5s.
            </p>
          </div>
        </Card>

        {/* Create Form */}
        {showCreateForm && (
          <Card className="bg-white rounded-lg border border-slate-200 p-6 mb-6" data-testid="create-form">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Create New API Key
            </h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="partner_name" className="text-slate-700 font-medium mb-2 block">
                    Partner/Project Name *
                  </Label>
                  <Input
                    id="partner_name"
                    type="text"
                    value={formData.partner_name}
                    onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                    placeholder="e.g., EventBrite Integration"
                    className="border-slate-200"
                    data-testid="partner-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                    Contact Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g., developer@company.com"
                    className="border-slate-200"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="description" className="text-slate-700 font-medium mb-2 block">
                  Description / Usage Details
                </Label>
                <Input
                  id="description"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Used for concert ticket booking integration"
                  className="border-slate-200"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="allowed_domain" className="text-slate-700 font-medium mb-2 block">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Allowed Domain (Website URL)
                  </Label>
                  <Input
                    id="allowed_domain"
                    type="text"
                    value={formData.allowed_domain}
                    onChange={(e) => setFormData({ ...formData, allowed_domain: e.target.value })}
                    placeholder="e.g., https://mytickets.com"
                    className="border-slate-200"
                  />
                  <p className="text-xs text-slate-500 mt-1">Domain where this key will be used</p>
                </div>
                <div>
                  <Label htmlFor="expires_in_days" className="text-slate-700 font-medium mb-2 block">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Expires In (Days)
                  </Label>
                  <Input
                    id="expires_in_days"
                    type="number"
                    min="1"
                    value={formData.expires_in_days}
                    onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                    placeholder="Leave empty for never expires"
                    className="border-slate-200"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty for no expiration</p>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={loading}
                  data-testid="submit-api-key"
                >
                  {loading ? 'Creating...' : 'Create API Key'}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  variant="outline"
                  className="border-slate-200 text-slate-700"
                  data-testid="cancel-create"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* API Keys List */}
        {apiKeys.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-api-keys">
            <Key className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              No API keys yet
            </h2>
            <p className="text-slate-500 mb-6" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Create your first API key to enable external integrations
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="api-keys-list">
            {apiKeys.map((apiKey) => (
              <Card
                key={apiKey.id}
                className={`bg-white rounded-lg border p-6 ${!apiKey.active || isExpired(apiKey.expires_at) ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}
                data-testid={`api-key-card-${apiKey.id}`}
              >
                {editingKey === apiKey.id ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-900 mb-3">Edit API Key Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Partner/Project Name *</Label>
                        <Input
                          value={formData.partner_name}
                          onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                          placeholder="Partner Name"
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Contact Email</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Email"
                          className="border-slate-200"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-1 block">Description</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Description"
                        className="border-slate-200"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Allowed Domain</Label>
                        <Input
                          value={formData.allowed_domain}
                          onChange={(e) => setFormData({ ...formData, allowed_domain: e.target.value })}
                          placeholder="https://example.com"
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">New Expiry (Days from now, 0 = never)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.expires_in_days}
                          onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                          placeholder="Leave empty to keep current"
                          className="border-slate-200"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUpdate(apiKey.id)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        disabled={loading}
                      >
                        <Save className="w-4 h-4 mr-1" />
                        {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button onClick={cancelEditing} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header with name and status */}
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                          {apiKey.partner_name}
                        </h3>
                        {apiKey.active && !isExpired(apiKey.expires_at) ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Active
                          </span>
                        ) : isExpired(apiKey.expires_at) ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                            Expired
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 flex items-center gap-1">
                            <X className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </div>
                      
                      {/* Key display with mask/unmask */}
                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between">
                          <code className="text-sm text-slate-700 break-all" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {visibleKeys[apiKey.id] ? apiKey.key : maskKey(apiKey.key)}
                          </code>
                          <div className="flex gap-1 ml-2">
                            <Button
                              onClick={() => toggleKeyVisibility(apiKey.id)}
                              variant="ghost"
                              size="sm"
                              className="text-slate-600 hover:text-slate-900"
                              title={visibleKeys[apiKey.id] ? "Hide key" : "Show key"}
                            >
                              {visibleKeys[apiKey.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              onClick={() => copyToClipboard(apiKey.key)}
                              variant="ghost"
                              size="sm"
                              className="text-slate-600 hover:text-slate-900"
                              title="Copy key"
                              data-testid={`copy-key-${apiKey.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {apiKey.email && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4" />
                            <span>{apiKey.email}</span>
                          </div>
                        )}
                        {apiKey.allowed_domain && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Globe className="w-4 h-4" />
                            <span>{apiKey.allowed_domain}</span>
                          </div>
                        )}
                        {apiKey.description && (
                          <div className="flex items-center gap-2 text-slate-600 md:col-span-2">
                            <Info className="w-4 h-4" />
                            <span>{apiKey.description}</span>
                          </div>
                        )}
                      </div>

                      {/* Timestamps */}
                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                        <span>Created: {new Date(apiKey.created_at).toLocaleString()}</span>
                        {apiKey.expires_at && (
                          <span className={isExpired(apiKey.expires_at) ? 'text-red-600 font-medium' : ''}>
                            {isExpired(apiKey.expires_at) ? 'Expired' : 'Expires'}: {new Date(apiKey.expires_at).toLocaleString()}
                          </span>
                        )}
                        {!apiKey.expires_at && (
                          <span className="text-green-600">Never expires</span>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        onClick={() => startEditing(apiKey)}
                        variant="outline"
                        size="sm"
                        className="border-slate-200 text-slate-600 hover:bg-slate-50"
                        title="Edit details"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleRegenerate(apiKey.id)}
                        variant="outline"
                        size="sm"
                        className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                        title="Generate a new key"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate
                      </Button>
                      {apiKey.active ? (
                        <Button
                          onClick={() => handleDeactivate(apiKey.id)}
                          variant="outline"
                          size="sm"
                          className="border-orange-200 text-orange-600 hover:bg-orange-50"
                          data-testid={`deactivate-key-${apiKey.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Deactivate
                        </Button>
                      ) : (
                        <>
                          <Button
                            onClick={() => handleActivate(apiKey.id)}
                            variant="outline"
                            size="sm"
                            className="border-green-200 text-green-600 hover:bg-green-50"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Activate
                          </Button>
                          <Button
                            onClick={() => handleDelete(apiKey.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            title="Permanently delete this key"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Integration Instructions */}
        <Card className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-indigo-900 mb-3" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            Integration Guide
          </h3>
          <div className="space-y-3 text-sm text-indigo-900" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            <p>To integrate with the TicketKart Seating Engine API:</p>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Include the API key in the <code className="bg-indigo-100 px-2 py-1 rounded text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>X-API-Key</code> header</li>
              <li>Use the public endpoints to manage seat reservations</li>
              <li>Base URL: <code className="bg-indigo-100 px-2 py-1 rounded text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{getBaseUrl()}/api/public</code></li>
            </ol>
          </div>
        </Card>
      </main>
    </div>
  );
}
